import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

/**
 * Extracts a storage object path (bucket, path) from either:
 *  - a raw path (e.g. "ab/uuid.jpg" — assumed to be in defaultBucket)
 *  - a Supabase public URL (`/storage/v1/object/public/<bucket>/<path>`)
 *  - a Supabase signed URL (`/storage/v1/object/sign/<bucket>/<path>?token=...`)
 * Returns null when the URL is external (unrelated origin).
 */
function parseStorageRef(src: string, defaultBucket: string): { bucket: string; path: string } | null {
  if (!src) return null;
  try {
    // Absolute URL
    if (/^https?:\/\//i.test(src)) {
      const u = new URL(src);
      const m = u.pathname.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/);
      if (m) return { bucket: decodeURIComponent(m[1]), path: decodeURIComponent(m[2]) };
      return null;
    }
    // Relative — treat as path inside defaultBucket
    return { bucket: defaultBucket, path: src.replace(/^\/+/, '') };
  } catch {
    return null;
  }
}

async function resolveSignedUrl(bucket: string, path: string): Promise<string> {
  const key = `${bucket}/${path}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const running = inflight.get(key);
  if (running) return running;
  const p = (async () => {
    let signedUrl: string | null = null;

    const { data: backendData, error: backendError } = await supabase.functions.invoke('sign-product-image', {
      body: { bucket, path },
    });
    if (!backendError && backendData?.signedUrl) {
      signedUrl = backendData.signedUrl;
    } else {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60 * 24 * 7);
      if (!error && data?.signedUrl) {
        signedUrl = data.signedUrl;
      } else {
        const { data: fallbackData, error: fallbackError } = await supabase.functions.invoke('sign-product-image', {
        body: { bucket, path },
        });
        if (fallbackError || !fallbackData?.signedUrl) throw fallbackError ?? error ?? backendError ?? new Error('sign failed');
        signedUrl = fallbackData.signedUrl;
      }
    }

    if (!signedUrl) throw new Error('sign failed');
    cache.set(key, signedUrl);
    // Refresh cache before the 7-day expiry
    setTimeout(() => cache.delete(key), 1000 * 60 * 60 * 24 * 6);
    return signedUrl;
  })();
  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}

interface Props extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null | undefined;
  defaultBucket?: string;
  fallback?: React.ReactNode;
}

export function SignedImage({ src, defaultBucket = 'product-images', fallback, ...rest }: Props) {
  const [resolved, setResolved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (!src) { setResolved(null); return; }
    const ref = parseStorageRef(src, defaultBucket);
    if (!ref) {
      // External URL — use as-is
      setResolved(src);
      return;
    }
    resolveSignedUrl(ref.bucket, ref.path)
      .then((u) => { if (!cancelled) setResolved(u); })
      .catch(() => {
        if (cancelled) return;
        // Missing object in storage — fall back to original URL if absolute, else render fallback.
        if (/^https?:\/\//i.test(src)) setResolved(src);
        else setFailed(true);
      });
    return () => { cancelled = true; };
  }, [src, defaultBucket]);

  if (!src || failed) return <>{fallback ?? null}</>;
  if (!resolved) return <>{fallback ?? null}</>;
  return <img src={resolved} {...rest} />;
}
